import type { Layer, ColorStats } from "@/features/draw/types";
import {
  getColorDifference,
  isColorSimilar,
} from "@/features/draw/utils/color/colorUtils";

/**
 * Composable for managing color-related operations
 */
export function useColorManagement() {
  /**
   * Get unique colors from all visible layers
   * @param layers - Array of layers
   * @returns Array of unique colors
   */
  const getUniqueColors = (layers: Layer[]): string[] => {
    const colors = new Set<string>();
    layers.forEach((layer) => {
      if (layer.visible) {
        layer.pixels.forEach((color) => {
          if (color !== "") {
            colors.add(color);
          }
        });
      }
    });
    return Array.from(colors);
  };

  /**
   * Calculate color statistics for visible layers
   * @param layers - Array of layers
   * @param selectedArea - Optional selected area to limit statistics to
   * @returns Object with colors as keys and counts as values
   */
  const getColorStatistics = (
    layers: Layer[],
    selectedArea?: boolean[]
  ): ColorStats => {
    const stats: ColorStats = {};

    layers.forEach((layer) => {
      if (layer.visible) {
        layer.pixels.forEach((color, index) => {
          if (color !== "" && (!selectedArea || selectedArea[index])) {
            stats[color] = (stats[color] || 0) + 1;
          }
        });
      }
    });

    return stats;
  };

  /**
   * Map old colors to new colors based on similarity
   * @param oldColors - Array of old colors
   * @param newColors - Array of new colors
   * @returns Mapping from old colors to new colors
   */
  const mapColorsBasedOnSimilarity = (
    oldColors: string[],
    newColors: string[]
  ): Record<string, string> => {
    const colorMap: Record<string, string> = {};

    oldColors.forEach((oldColor) => {
      let minDistance = Infinity;
      let closestNewColor = newColors[0] || oldColor;

      newColors.forEach((newColor) => {
        const distance = getColorDifference(oldColor, newColor);
        if (distance < minDistance) {
          minDistance = distance;
          closestNewColor = newColor;
        }
      });

      colorMap[oldColor] = closestNewColor;
    });

    return colorMap;
  };

  /**
   * Merge similar colors based on a threshold
   * @param colors - Array of colors to merge
   * @param threshold - Similarity threshold (0-100)
   * @returns Array of merged colors
   */
  const mergeSimilarColors = (
    colors: string[],
    threshold: number
  ): string[] => {
    if (colors.length <= 1) return colors;

    const result: string[] = [colors[0]];

    for (let i = 1; i < colors.length; i++) {
      const currentColor = colors[i];
      let shouldAdd = true;

      for (const existingColor of result) {
        if (isColorSimilar(currentColor, existingColor, threshold)) {
          shouldAdd = false;
          break;
        }
      }

      if (shouldAdd) {
        result.push(currentColor);
      }
    }

    return result;
  };

  /**
   * Apply color mapping to layers
   * @param layers - Array of layers
   * @param colorMap - Mapping from old colors to new colors
   * @returns Updated array of layers
   */
  const applyColorMapping = (
    layers: Layer[],
    colorMap: Record<string, string>
  ): Layer[] => {
    return layers.map((layer) => {
      if (!layer.visible) return layer;

      const newPixels = layer.pixels.map((color) => {
        if (color === "") return color;
        return colorMap[color] || color;
      });

      return {
        ...layer,
        pixels: newPixels,
      };
    });
  };

  /**
   * Merge similar colors based on color difference threshold
   * @param layers - Array of layers
   * @param threshold - Similarity threshold (0-100)
   * @param forceAddToHistory - Whether to force add to history
   * @returns Updated array of layers
   */
  const mergeColorsByThreshold = (
    layers: Layer[],
    threshold: number
  ): Layer[] => {
    const colorInfo = new Map();
    layers.forEach((layer) => {
      if (layer.visible) {
        layer.pixels.forEach((color) => {
          if (color !== "") {
            colorInfo.set(color, (colorInfo.get(color) || 0) + 1);
          }
        });
      }
    });

    const sortedColors = Array.from(colorInfo.entries()).sort(
      (a, b) => b[1] - a[1]
    );

    const colorMap = new Map();

    for (const [color] of sortedColors) {
      if (colorMap.has(color)) continue;

      colorMap.set(color, color);

      for (const [otherColor] of sortedColors) {
        if (colorMap.has(otherColor) || color === otherColor) continue;

        const diff = getColorDifference(color, otherColor);

        if (diff <= threshold) {
          colorMap.set(otherColor, color);
        }
      }
    }

    const updatedLayers = layers.map((layer) => {
      if (!layer.visible) return layer;

      const newPixels = layer.pixels.map((color) => {
        if (color === "") return color;
        return colorMap.get(color) || color;
      });

      return {
        ...layer,
        pixels: newPixels,
      };
    });

    return updatedLayers;
  };

  /**
   * Merge colors by count
   * @param layers - Array of layers
   * @param oldColorStats - Old color statistics
   * @param newColorStats - New color statistics
   * @returns Updated array of layers
   */
  const mergeColorsByCount = (
    layers: Layer[],
    oldColorStats: ColorStats,
    newColorStats: ColorStats
  ): Layer[] => {
    const oldColors = Object.keys(oldColorStats);
    const newColors = Object.keys(newColorStats);

    // Map colors based on similarity
    const oldToNewColorMap = mapColorsBasedOnSimilarity(oldColors, newColors);

    // Apply the mapping to layers
    return applyColorMapping(layers, oldToNewColorMap);
  };

  /**
   * Replace selected colors with target color
   * @param layers - Array of layers
   * @param colors - Colors to replace
   * @param targetColor - Target color
   * @returns Updated array of layers
   */
  const replaceColors = (
    layers: Layer[],
    colors: string[],
    targetColor: string
  ): Layer[] => {
    const updatedLayers = layers.map((layer) => {
      if (!layer.visible) return layer;

      const newPixels = layer.pixels.map((color) => {
        if (colors.includes(color)) {
          return targetColor;
        }
        return color;
      });

      return {
        ...layer,
        pixels: newPixels,
      };
    });

    return updatedLayers;
  };

  /**
   * Delete selected colors (replace with empty)
   * @param layers - Array of layers
   * @param colors - Colors to delete
   * @returns Updated array of layers
   */
  const deleteColors = (layers: Layer[], colors: string[]): Layer[] => {
    const updatedLayers = layers.map((layer) => {
      if (!layer.visible) return layer;

      const newPixels = layer.pixels.map((color) => {
        if (colors.includes(color)) {
          return "";
        }
        return color;
      });

      return {
        ...layer,
        pixels: newPixels,
      };
    });

    return updatedLayers;
  };

  /**
   * Remove noise pixels based on neighbor analysis
   * @param layer - Target layer
   * @param threshold - Noise threshold (0-100)
   * @param selectedArea - Optional selected area to limit processing to
   * @returns Updated pixels array
   */
  const removeNoise = (
    layer: Layer,
    threshold: number,
    selectedArea?: boolean[]
  ): string[] => {
    if (!layer) return [];

    const hasSelection =
      selectedArea && selectedArea.some((isSelected) => isSelected);

    const colorFrequencies = new Map<string, number>();
    layer.pixels.forEach((color, index) => {
      if (color !== "" && (!hasSelection || selectedArea[index])) {
        colorFrequencies.set(color, (colorFrequencies.get(color) || 0) + 1);
      }
    });

    const dominantColors = Array.from(colorFrequencies.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([color]) => color);

    if (dominantColors.length === 0) return [...layer.pixels];

    const newPixels = [...layer.pixels];
    const width = Math.sqrt(layer.pixels.length);
    const height = width;

    const ratio = threshold / 100;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const index = y * width + x;

        if (hasSelection && !selectedArea[index]) continue;

        const currentColor = layer.pixels[index];
        if (currentColor === "") continue;

        const neighbors: string[] = [];
        let transparentNeighbors = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            const nx = x + dx;
            const ny = y + dy;
            if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
              const neighborIndex = ny * width + nx;
              if (!hasSelection || selectedArea[neighborIndex]) {
                const neighborColor = layer.pixels[neighborIndex];
                if (neighborColor === "") {
                  transparentNeighbors++;
                } else {
                  neighbors.push(neighborColor);
                }
              } else {
                transparentNeighbors++;
              }
            } else {
              transparentNeighbors++;
            }
          }
        }

        const neighborColorFreq = new Map<string, number>();
        neighbors.forEach((color) => {
          neighborColorFreq.set(color, (neighborColorFreq.get(color) || 0) + 1);
        });

        let shouldMerge = false;
        let targetColor = "";

        const minNeighborsNeeded = Math.floor(8 * (1 - ratio));

        if (transparentNeighbors >= 6) {
          shouldMerge = true;
        } else if (neighbors.length === 0) {
          shouldMerge = true;
          targetColor = dominantColors[0];
        } else {
          const mostCommonNeighbor = Array.from(
            neighborColorFreq.entries()
          ).sort((a, b) => b[1] - a[1])[0];

          if (mostCommonNeighbor) {
            const [commonColor, count] = mostCommonNeighbor;

            if (
              count < minNeighborsNeeded ||
              (colorFrequencies.get(currentColor) || 0) <
              (colorFrequencies.get(commonColor) || 0)
            ) {
              shouldMerge = true;
              targetColor = commonColor;
            }
          }

          if (!shouldMerge && ratio > 0.7) {
            const currentColorFreq = colorFrequencies.get(currentColor) || 0;
            const dominantColorFreq =
              colorFrequencies.get(dominantColors[0]) || 0;

            if (currentColorFreq < dominantColorFreq * ratio) {
              shouldMerge = true;
              targetColor = dominantColors[0];
            }
          }
        }

        if (shouldMerge) {
          newPixels[index] = targetColor;
        }
      }
    }

    return newPixels;
  };

  /**
   * Auto fill empty pixels based on neighbor analysis
   * @param layer - Target layer
   * @param threshold - Fill threshold (0-100)
   * @returns Updated pixels array
   */
  const autoFill = (layer: Layer, threshold: number): string[] => {
    if (!layer) return [];

    const width = Math.sqrt(layer.pixels.length);
    const height = width;
    const newPixels = [...layer.pixels];

    const ratio = threshold / 100;

    const colorFrequencies = new Map<string, number>();
    layer.pixels.forEach((color) => {
      if (color !== "") {
        colorFrequencies.set(color, (colorFrequencies.get(color) || 0) + 1);
      }
    });

    const dominantColors = Array.from(colorFrequencies.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([color]) => color);

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const index = y * width + x;
        if (newPixels[index] !== "") continue;

        const neighbors: string[] = [];
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            const nx = x + dx;
            const ny = y + dy;
            if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
              const neighborColor = layer.pixels[ny * width + nx];
              if (neighborColor !== "") {
                neighbors.push(neighborColor);
              }
            }
          }
        }

        if (neighbors.length > 0) {
          const neighborColorFreq = new Map<string, number>();
          neighbors.forEach((color) => {
            neighborColorFreq.set(
              color,
              (neighborColorFreq.get(color) || 0) + 1
            );
          });

          const [commonColor] = Array.from(neighborColorFreq.entries()).sort(
            (a, b) => b[1] - a[1]
          )[0];

          const minNeighborsNeeded = Math.max(1, Math.floor((1 - ratio) * 8));
          if (neighbors.length >= minNeighborsNeeded) {
            newPixels[index] = commonColor;
          }
        } else if (ratio > 0.7 && dominantColors.length > 0) {
          newPixels[index] = dominantColors[0];
        }
      }
    }

    return newPixels;
  };

  /**
   * Add content from other layers to target layer based on color matching
   * @param layers - Array of layers
   * @param targetLayerId - Target layer ID
   * @param colors - Colors to match
   * @param tolerance - Color tolerance
   * @param removeOriginal - Whether to remove original content
   * @returns Object with updated target layer pixels and source layers pixels
   */
  const addContentFromLayers = (
    layers: Layer[],
    targetLayerId: number,
    colors: string[],
    tolerance: number,
    removeOriginal: boolean
  ): {
    targetPixels: string[];
    updatedSourceLayers: { id: number; pixels: Uint32Array }[];
  } => {
    const targetLayer = layers.find((layer) => layer.id === targetLayerId);
    if (!targetLayer) return { targetPixels: [], updatedSourceLayers: [] };

    const newPixels = [...targetLayer.pixels];
    const updatedSourceLayers: { id: number; pixels: string[] }[] = [];

    layers.forEach((layer) => {
      if (!layer.visible || layer.id === targetLayerId) return;

      let updatedSourcePixels: Uint32Array | null = null;
      if (removeOriginal) {
        updatedSourcePixels = layer.pixels;
      }

      layer.pixels.forEach((color, index) => {
        const isSelected = colors.includes(color);

        if (isSelected) {
          if (newPixels[index] === "") {
            newPixels[index] = color;
          }

          if (removeOriginal && updatedSourcePixels) {
            updatedSourcePixels[index] = "";
          }
        }
      });

      if (removeOriginal && updatedSourcePixels) {
        updatedSourceLayers.push({
          id: layer.id,
          pixels: updatedSourcePixels,
        });
      }
    });

    return {
      targetPixels: newPixels,
      updatedSourceLayers,
    };
  };

  return {
    getUniqueColors,
    getColorStatistics,
    mapColorsBasedOnSimilarity,
    mergeSimilarColors,
    applyColorMapping,
    mergeColorsByThreshold,
    mergeColorsByCount,
    replaceColors,
    deleteColors,
    removeNoise,
    autoFill,
    addContentFromLayers,
  };
}
